import { useState, useMemo, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  BookOpen,
  Scale,
  Search,
  ChevronsDownUp,
  ChevronsUpDown,
  X,
  MessageSquare,
  Info,
  FileText,
  FolderTree,
} from "lucide-react";
import { clsx } from "clsx";
import { lookupPasalRefs, type PasalData } from "@/data/uuPnbpPasal";

// ─── Types ────────────────────────────────────────────────────────────────────

interface TreeLeaf {
  name: string;
  description?: string;
}

interface TreeSubItem {
  name: string;
  description?: string;
  pasal?: string[];
  children?: TreeLeaf[];
  docLink?: string;
}

interface TreeCategory {
  id: number;
  title: string;
  subtitle?: string;
  color:
    | "blue"
    | "indigo"
    | "violet"
    | "emerald"
    | "amber"
    | "rose"
    | "cyan"
    | "orange"
    | "red"
    | "teal";
  items: TreeSubItem[];
}

// ─── Tree data ────────────────────────────────────────────────────────────────

const PNBP_TREE: TreeCategory[] = [
  {
    id: 1,
    title: "Landasan & Tujuan",
    color: "blue",
    items: [
      {
        name: "Latar Belakang",
        description: "Dasar pembentukan pengaturan PNBP baru",
        pasal: ["Penjelasan Umum"],
        children: [{ name: "Penjelasan Umum UU 9/2018" }],
      },
      {
        name: "Fungsi PNBP",
        description: "Dua fungsi utama PNBP dalam sistem keuangan negara",
        pasal: ["Penjelasan Fungsi PNBP"],
        children: [
          {
            name: "Budgetary",
            description: "Fungsi penganggaran — kontribusi pendapatan negara",
          },
          {
            name: "Regulatory",
            description: "Fungsi pengaturan — pengendalian kekayaan negara",
          },
        ],
      },
      {
        name: "Tujuan Pengaturan PNBP",
        description: "Tiga tujuan utama UU PNBP",
        pasal: ["Pasal 2"],
        children: [
          {
            name: "Kemandirian fiskal",
            description:
              "Mengoptimalkan sumber pendapatan dan memperkuat ketahanan fiskal",
          },
          {
            name: "Kesejahteraan masyarakat",
            description:
              "Mendukung perbaikan kesejahteraan rakyat dan pertumbuhan ekonomi berkualitas",
          },
          {
            name: "Tata kelola pemerintahan",
            description:
              "Mewujudkan pelayanan yang bersih, profesional, transparan, dan akuntabel",
          },
        ],
      },
    ],
  },
  {
    id: 2,
    title: "Definisi & Konsep Dasar",
    color: "indigo",
    items: [
      {
        name: "Definisi-Definisi Pokok",
        description: "Pengertian istilah-istilah kunci dalam UU PNBP",
        pasal: ["Pasal 1"],
        children: [
          {
            name: "PNBP",
            description:
              "Pungutan yang dibayar orang pribadi atau badan atas layanan/pemanfaatan SDA dan hak negara",
          },
          {
            name: "Wajib Bayar",
            description:
              "Orang pribadi atau badan yang berkewajiban membayar PNBP",
          },
          {
            name: "Instansi Pengelola PNBP",
            description: "Instansi yang menyelenggarakan pengelolaan PNBP",
          },
          {
            name: "Mitra Instansi Pengelola PNBP",
            description: "Badan yang membantu Instansi Pengelola",
          },
          {
            name: "Pelayanan",
            description:
              "Penyediaan barang, jasa, atau pelayanan administratif oleh Pemerintah",
          },
          {
            name: "Pemanfaatan SDA",
            description:
              "Pemanfaatan bumi, air, udara, ruang angkasa, dan kekayaan alam",
          },
          {
            name: "Barang Milik Negara (BMN)",
            description:
              "Kegiatan penggunaan, pemanfaatan, dan pemindahtanganan barang negara",
          },
          {
            name: "Pengelolaan Dana",
            description:
              "Pengelolaan atas dana pemerintah untuk tujuan tertentu",
          },
        ],
      },
    ],
  },
  {
    id: 3,
    title: "Objek & Subjek PNBP",
    color: "violet",
    items: [
      {
        name: "Objek PNBP",
        description:
          "Seluruh aktivitas, hal, dan benda sumber penerimaan negara di luar perpajakan",
        pasal: ["Pasal 3", "Pasal 4"],
        docLink: "/documents/tree",
        children: [
          { name: "Pemanfaatan Sumber Daya Alam (SDA)" },
          { name: "Pelayanan" },
          { name: "Pengelolaan Kekayaan Negara Dipisahkan" },
          { name: "Pengelolaan Barang Milik Negara (BMN)" },
          { name: "Pengelolaan Dana" },
          { name: "Hak Negara Lainnya" },
        ],
      },
      {
        name: "Subjek PNBP",
        description:
          "Pihak yang menggunakan, memperoleh manfaat, atau memiliki kaitan dengan objek PNBP",
        pasal: ["Pasal 5"],
        children: [
          { name: "Orang pribadi", description: "Dari dalam atau luar negeri" },
          { name: "Badan", description: "Dari dalam atau luar negeri" },
          {
            name: "→ menjadi Wajib Bayar",
            description: "Apabila memiliki kewajiban membayar PNBP",
          },
        ],
      },
    ],
  },
  {
    id: 4,
    title: "Tarif PNBP",
    color: "emerald",
    items: [
      {
        name: "Jenis Tarif",
        description: "Dua bentuk tarif atas jenis PNBP",
        pasal: ["Pasal 6"],
        children: [
          {
            name: "Tarif spesifik",
            description:
              "Ditetapkan dengan nilai nominal uang (contoh: Rp5.000.000/satuan)",
          },
          {
            name: "Tarif ad valorem",
            description:
              "Ditetapkan dengan persentase atau formula (contoh: 10% × dasar perhitungan)",
          },
        ],
      },
      {
        name: "Basis Penentuan Tarif",
        description: "Pertimbangan dalam penyusunan tarif",
        pasal: ["Pasal 7", "Pasal 8"],
        children: [
          { name: "Nilai manfaat / kadar / kualitas SDA" },
          { name: "Biaya penyelenggaraan layanan" },
          { name: "Dampak sosial, ekonomi, dan lingkungan" },
          { name: "Kebijakan Pemerintah" },
          { name: "Aspek keadilan" },
        ],
      },
      {
        name: "Kategori Tarif Berdasarkan Objek",
        description: "Ketentuan tarif per jenis objek PNBP",
        pasal: ["Pasal 7", "Pasal 8"],
        children: [
          {
            name: "SDA (terbarukan & tak terbarukan)",
            description: "→ Pasal 7: diatur dengan UU, kontrak, dan/atau PP",
          },
          {
            name: "Pelayanan (dasar & nondasar)",
            description:
              "→ Pasal 8: diatur dengan PP dan/atau Peraturan Menteri",
          },
          {
            name: "Kekayaan Negara Dipisahkan",
            description: "→ Pasal 9: diatur dengan UU dan/atau RUPS",
          },
          {
            name: "Barang Milik Negara",
            description:
              "→ Pasal 10: diatur dengan PP dan/atau Peraturan Menteri",
          },
          {
            name: "Pengelolaan Dana",
            description: "→ Pasal 11: diatur dengan Peraturan Menteri",
          },
          {
            name: "Hak Negara Lainnya",
            description:
              "→ Pasal 12: diatur dengan UU, PP, dan/atau Peraturan Menteri",
          },
        ],
      },
      {
        name: "Ketentuan Khusus",
        description: "Tarif dengan pertimbangan tertentu",
        pasal: ["Pasal 13"],
        children: [
          {
            name: "Tarif Rp0 / 0%",
            description:
              "Untuk kegiatan sosial, keagamaan, kenegaraan, kondisi kahar, masyarakat tidak mampu, mahasiswa berprestasi, dan UMKM",
          },
        ],
      },
    ],
  },
  {
    id: 5,
    title: "Kewenangan & Aktor",
    color: "amber",
    items: [
      {
        name: "Menteri (Pengelola Fiskal / CFO)",
        description: "Kewenangan Menteri Keuangan dalam pengelolaan PNBP",
        pasal: ["Pasal 15"],
        children: [
          { name: "Menyusun kebijakan umum Pengelolaan PNBP" },
          {
            name: "Mengevaluasi, menyusun, dan menetapkan jenis dan tarif PNBP",
          },
          { name: "Menetapkan target PNBP dan pagu penggunaan dana" },
          { name: "Menetapkan penggunaan dana PNBP" },
          {
            name: "Melakukan pengawasan perencanaan, pelaksanaan, dan pertanggungjawaban",
          },
        ],
      },
      {
        name: "Instansi Pengelola PNBP (COO)",
        description: "Kementerian/Lembaga dan Kementerian BUN",
        pasal: ["Pasal 16", "Pasal 17"],
        children: [
          { name: "Menyusun dan menyampaikan usulan jenis dan tarif PNBP" },
          { name: "Mengusulkan penggunaan dana PNBP" },
          { name: "Memungut dan menyetorkan PNBP ke Kas Negara" },
          { name: "Mengelola piutang PNBP" },
          { name: "Menyusun laporan pertanggungjawaban PNBP" },
        ],
      },
      {
        name: "Mitra Instansi Pengelola PNBP",
        description: "Badan yang membantu sebagian kegiatan pengelolaan",
        pasal: ["Pasal 19"],
        children: [
          { name: "Pemungutan PNBP" },
          { name: "Penyetoran PNBP ke Kas Negara" },
          { name: "Pelaporan kepada Instansi Pengelola" },
          { name: "Penatausahaan PNBP" },
        ],
      },
    ],
  },
  {
    id: 6,
    title: "Siklus Pengelolaan PNBP",
    color: "cyan",
    items: [
      {
        name: "Perencanaan",
        description: "Penyusunan rencana PNBP dalam siklus APBN",
        pasal: ["Pasal 22", "Pasal 23"],
        children: [
          {
            name: "Target PNBP",
            description:
              "Disusun secara realistis, optimal, dan sesuai ketentuan",
          },
          {
            name: "Pagu Penggunaan Dana PNBP",
            description:
              "Untuk Instansi yang telah mendapat persetujuan penggunaan dana",
          },
        ],
      },
      {
        name: "Pelaksanaan",
        description: "Tahap operasional pengelolaan PNBP",
        pasal: [
          "Pasal 26",
          "Pasal 28",
          "Pasal 29",
          "Pasal 31",
          "Pasal 33",
          "Pasal 35",
        ],
        children: [
          {
            name: "Penetapan PNBP Terutang",
            description:
              "→ Pasal 26: dihitung oleh Instansi, Mitra, atau Wajib Bayar",
          },
          {
            name: "Pemungutan PNBP",
            description: "→ Pasal 28: wajib dilaksanakan Instansi Pengelola",
          },
          {
            name: "Pembayaran PNBP Terutang",
            description:
              "→ Pasal 31: denda 2%/bulan jika terlambat (maks. 24 bulan)",
          },
          {
            name: "Penyetoran ke Kas Negara",
            description: "→ Pasal 29: seluruh PNBP wajib disetor ke Kas Negara",
          },
          {
            name: "Penggunaan Dana PNBP",
            description: "→ Pasal 33: atas persetujuan Menteri",
          },
          {
            name: "Pengelolaan Piutang PNBP",
            description: "→ Pasal 35: dicatat jika Wajib Bayar belum membayar",
          },
        ],
      },
      {
        name: "Pertanggungjawaban",
        description: "Pencatatan dan pelaporan realisasi PNBP",
        pasal: ["Pasal 41", "Pasal 42", "Pasal 43"],
        children: [
          {
            name: "Penatausahaan",
            description:
              "→ Pasal 41: wajib oleh Instansi dan Wajib Bayar; dokumen disimpan 10 tahun",
          },
          {
            name: "Pelaporan Wajib Bayar",
            description:
              "→ Pasal 42: paling lambat 20 hari kalender; denda Rp1 juta jika terlambat",
          },
          {
            name: "Pelaporan Instansi Pengelola",
            description:
              "→ Pasal 43: laporan realisasi penerimaan dan penggunaan dana kepada Menteri",
          },
        ],
      },
      {
        name: "Pengawasan",
        description: "Pengawasan intern dan oleh Pemerintah Pusat",
        pasal: ["Pasal 45", "Pasal 46"],
        children: [
          {
            name: "Pengawasan Intern",
            description:
              "→ Pasal 45: oleh APIP yang bertanggung jawab kepada Menteri/Pimpinan Lembaga",
          },
          {
            name: "Pengawasan Menteri",
            description:
              "→ Pasal 46: verifikasi, penilaian, dan/atau evaluasi terhadap Instansi Pengelola",
          },
        ],
      },
    ],
  },
  {
    id: 7,
    title: "Pengendalian & Pemeriksaan",
    color: "teal",
    items: [
      {
        name: "Dasar & Lingkup Pemeriksaan PNBP",
        description: "Pemeriksaan oleh instansi pemeriksa (BPKP)",
        pasal: ["Pasal 47"],
        children: [
          {
            name: "Pemeriksaan terhadap Wajib Bayar",
            description:
              "→ Pasal 47–48: atas permintaan Pimpinan Instansi atau Menteri",
          },
          {
            name: "Pemeriksaan terhadap Instansi Pengelola",
            description: "→ Pasal 50: atas permintaan Menteri",
          },
          {
            name: "Pemeriksaan terhadap Mitra",
            description:
              "→ Pasal 51: atas permintaan Menteri dan/atau Pimpinan Instansi",
          },
        ],
      },
      {
        name: "Ruang Lingkup Pemeriksaan",
        description: "Cakupan objek pemeriksaan",
        pasal: ["Pasal 52"],
        children: [
          { name: "Laporan keuangan dan dokumen pendukung" },
          { name: "Bukti transaksi keuangan pembayaran/penyetoran" },
          { name: "Sistem pengendalian intern terkait pengelolaan PNBP" },
        ],
      },
      {
        name: "Output Pemeriksaan & Penetapan",
        description:
          "Surat-surat yang diterbitkan berdasarkan hasil pemeriksaan",
        pasal: ["Pasal 36", "Pasal 37", "Pasal 38"],
        children: [
          {
            name: "Surat Tagihan PNBP",
            description:
              "→ Pasal 37: untuk tagihan pokok maupun sanksi administratif denda",
          },
          {
            name: "Surat Ketetapan PNBP (Kurang/Nihil/Lebih Bayar)",
            description:
              "→ Pasal 36: penetapan PNBP Terutang; masa daluwarsa 10 tahun",
          },
          {
            name: "Koreksi Surat Tagihan",
            description:
              "→ Pasal 38: Wajib Bayar dapat mengajukan permohonan koreksi secara tertulis",
          },
        ],
      },
    ],
  },
  {
    id: 8,
    title: "Hak Wajib Bayar",
    color: "orange",
    items: [
      {
        name: "Keberatan",
        description: "Hak mengajukan keberatan atas Surat Ketetapan PNBP",
        pasal: ["Pasal 58", "Pasal 59", "Pasal 60"],
        children: [
          {
            name: "Pengajuan keberatan secara tertulis",
            description: "Dalam jangka waktu 3 bulan sejak tanggal SK PNBP",
          },
          {
            name: "Penetapan dalam 6 bulan",
            description:
              "Jika tidak ada penetapan, keberatan dianggap dikabulkan",
          },
          {
            name: "Penetapan bersifat final",
            description:
              "Dapat digugat ke Pengadilan Tinggi TUN jika tidak setuju",
          },
        ],
      },
      {
        name: "Keringanan PNBP",
        description: "Permohonan keringanan atas PNBP Terutang",
        pasal: ["Pasal 62"],
        children: [
          {
            name: "Penundaan",
            description:
              "Atas kondisi di luar kemampuan / kahar / kesulitan likuiditas",
          },
          { name: "Pengangsuran", description: "Pembayaran secara cicilan" },
          { name: "Pengurangan", description: "Perlu persetujuan Menteri" },
          {
            name: "Pembebasan",
            description:
              "Perlu persetujuan Menteri dan pertimbangan APIP / rekomendasi pemeriksa",
          },
        ],
      },
      {
        name: "Pengembalian (Refund)",
        description: "Pengembalian atas kelebihan pembayaran PNBP",
        pasal: ["Pasal 63"],
        children: [
          {
            name: "Kompensasi (pembayaran di muka)",
            description:
              "Default: diperhitungkan sebagai pembayaran berikutnya",
          },
          {
            name: "Pengembalian langsung (tunai)",
            description:
              "Dalam kondisi tertentu: pengakhiran usaha, putusan pengadilan, dll.",
          },
          {
            name: "Batas waktu permohonan",
            description:
              "5 tahun (umum) / 2 tahun (putusan pengadilan atau laporan pemeriksaan)",
          },
        ],
      },
    ],
  },
  {
    id: 9,
    title: "Sanksi & Pidana",
    color: "rose",
    items: [
      {
        name: "Sanksi Administratif terhadap Wajib Bayar",
        description:
          "Denda atas pelanggaran kewajiban pembayaran dan pelaporan",
        pasal: ["Pasal 31", "Pasal 41", "Pasal 42"],
        children: [
          {
            name: "Denda keterlambatan pembayaran",
            description:
              "→ Pasal 31: 2%/bulan dari PNBP Terutang, maks. 24 bulan",
          },
          {
            name: "Denda tidak menatausahakan",
            description: "→ Pasal 41: Rp10.000.000",
          },
          {
            name: "Denda tidak menyampaikan laporan",
            description: "→ Pasal 42: Rp1.000.000",
          },
        ],
      },
      {
        name: "Sanksi terhadap Instansi & Mitra",
        description:
          "Sanksi atas pelanggaran kewajiban pemungutan dan penyetoran",
        pasal: ["Pasal 28"],
        children: [
          {
            name: "Tidak melakukan verifikasi",
            description:
              "→ Pasal 27: sanksi sesuai ketentuan perundang-undangan",
          },
          {
            name: "Tidak melaksanakan pemungutan",
            description:
              "→ Pasal 28: sanksi sesuai ketentuan perundang-undangan",
          },
          {
            name: "Tidak mengelola piutang PNBP",
            description:
              "→ Pasal 35: sanksi sesuai ketentuan perundang-undangan",
          },
        ],
      },
      {
        name: "Sanksi Pidana",
        description: "Ketentuan pidana atas tindak pidana PNBP",
        pasal: ["Pasal 67", "Pasal 68"],
        children: [
          {
            name: "Wajib Bayar sengaja tidak membayar/lapor",
            description:
              "→ Pasal 67: denda 4× PNBP Terutang + penjara 2–6 tahun",
          },
          {
            name: "Sengaja tidak memberikan dokumen/keterangan",
            description:
              "→ Pasal 68: denda maks. Rp1 miliar atau kurungan maks. 1 tahun",
          },
        ],
      },
    ],
  },
  {
    id: 10,
    title: "Implementasi Operasional",
    subtitle: "PP 44/2025",
    color: "teal",
    items: [
      {
        name: "Penetapan Tarif",
        description: "Proses penetapan tarif PNBP secara operasional",
        pasal: [
          "Pasal 18 PP 44/2025",
          "Pasal 19 PP 44/2025",
          "Pasal 20 PP 44/2025",
        ],
        children: [
          {
            name: "Usulan tarif oleh Instansi Pengelola",
            description:
              "→ Pasal 19 PP 44/2025: termasuk analisis efektivitas, kinerja, latar belakang, dasar perhitungan, dan dampak",
          },
          {
            name: "Evaluasi tarif oleh Menteri",
            description:
              "→ Pasal 20 PP 44/2025: hasil evaluasi dapat berupa penyesuaian dan/atau penyederhanaan tarif",
          },
          {
            name: "Persetujuan Menteri selaku pengelola fiskal",
            description:
              "→ Pasal 18 PP 44/2025: wajib sebelum Kementerian/Lembaga menetapkan tarif via Peraturan Menteri",
          },
        ],
      },
      {
        name: "Pengelolaan Mitra",
        description: "Ketentuan operasional Mitra Instansi Pengelola",
        pasal: [
          "Pasal 5 PP 44/2025",
          "Pasal 6 PP 44/2025",
          "Pasal 7 PP 44/2025",
          "Pasal 8 PP 44/2025",
        ],
        children: [
          {
            name: "Penunjukan Mitra berdasarkan UU atau penugasan",
            description:
              "→ Pasal 5 PP 44/2025: wajib mendapat persetujuan Menteri sebelum penugasan",
          },
          {
            name: "Tugas Mitra: pemungutan, penyetoran, penagihan",
            description:
              "→ Pasal 6 PP 44/2025: termasuk penentuan PNBP Terutang dan monitoring",
          },
          {
            name: "Imbal jasa Mitra",
            description:
              "→ Pasal 7 PP 44/2025: berupa pembagian pendapatan dan/atau dukungan pendanaan APBN",
          },
          {
            name: "Sanksi administratif terhadap Mitra",
            description:
              "→ Pasal 8 PP 44/2025: berjenjang dari teguran tertulis hingga pencabutan status",
          },
        ],
      },
      {
        name: "Tata Kelola Teknis",
        description: "Mekanisme kontrak dan pengawasan teknis",
        pasal: ["Pasal 17 PP 44/2025", "Pasal 57 PP 44/2025"],
        children: [
          {
            name: "Kontrak antara Instansi dan Mitra",
            description:
              "→ Pasal 17 PP 44/2025: tarif tidak dapat ditentukan di awal atau disepakati bersama Wajib Bayar",
          },
          {
            name: "Pengawasan pemenuhan kewajiban PNBP",
            description:
              "→ Pasal 57 PP 44/2025: pengawasan dilakukan terhadap pemenuhan kewajiban dan kepatuhan perundang-undangan",
          },
          {
            name: "Prosedur operasional standar dan pelaporan",
            description: "Diatur lebih lanjut dalam Peraturan Menteri",
          },
        ],
      },
    ],
  },
];

// ─── Color map ────────────────────────────────────────────────────────────────

const COLOR_MAP = {
  blue: {
    bg: "bg-blue-50",
    border: "border-blue-200",
    badge: "bg-blue-100 text-blue-700",
    icon: "bg-blue-600",
    num: "text-blue-600",
    btn: "bg-blue-600 hover:bg-blue-700",
  },
  indigo: {
    bg: "bg-indigo-50",
    border: "border-indigo-200",
    badge: "bg-indigo-100 text-indigo-700",
    icon: "bg-indigo-600",
    num: "text-indigo-600",
    btn: "bg-indigo-600 hover:bg-indigo-700",
  },
  violet: {
    bg: "bg-violet-50",
    border: "border-violet-200",
    badge: "bg-violet-100 text-violet-700",
    icon: "bg-violet-600",
    num: "text-violet-600",
    btn: "bg-violet-600 hover:bg-violet-700",
  },
  emerald: {
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    badge: "bg-emerald-100 text-emerald-700",
    icon: "bg-emerald-600",
    num: "text-emerald-600",
    btn: "bg-emerald-600 hover:bg-emerald-700",
  },
  amber: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    badge: "bg-amber-100 text-amber-700",
    icon: "bg-amber-500",
    num: "text-amber-600",
    btn: "bg-amber-500 hover:bg-amber-600",
  },
  rose: {
    bg: "bg-rose-50",
    border: "border-rose-200",
    badge: "bg-rose-100 text-rose-700",
    icon: "bg-rose-600",
    num: "text-rose-600",
    btn: "bg-rose-600 hover:bg-rose-700",
  },
  cyan: {
    bg: "bg-cyan-50",
    border: "border-cyan-200",
    badge: "bg-cyan-100 text-cyan-700",
    icon: "bg-cyan-600",
    num: "text-cyan-600",
    btn: "bg-cyan-600 hover:bg-cyan-700",
  },
  orange: {
    bg: "bg-orange-50",
    border: "border-orange-200",
    badge: "bg-orange-100 text-orange-700",
    icon: "bg-orange-500",
    num: "text-orange-600",
    btn: "bg-orange-500 hover:bg-orange-600",
  },
  red: {
    bg: "bg-red-50",
    border: "border-red-200",
    badge: "bg-red-100 text-red-700",
    icon: "bg-red-600",
    num: "text-red-600",
    btn: "bg-red-600 hover:bg-red-700",
  },
  teal: {
    bg: "bg-teal-50",
    border: "border-teal-200",
    badge: "bg-teal-100 text-teal-700",
    icon: "bg-teal-600",
    num: "text-teal-600",
    btn: "bg-teal-600 hover:bg-teal-700",
  },
};

// ─── Helper: angka dari label seperti "1.", "10.", "16." ─────────────────────

function extractLabelNumber(label: string): number | null {
  const m = label.trim().match(/^(\d+)\.$/);
  return m ? parseInt(m[1]) : null;
}

// ─── Pasal Detail Modal ───────────────────────────────────────────────────────

interface PasalModalProps {
  pasals: PasalData[];
  subItemName: string;
  onClose: () => void;
  onAskAI: (question: string) => void;
}

function PasalModal({
  pasals,
  subItemName,
  onClose,
  onAskAI,
}: PasalModalProps) {
  const aiQuestion = `Jelaskan tentang "${subItemName}" berdasarkan UU No. 9 Tahun 2018 tentang PNBP. Referensi: ${pasals.map((p) => p.referensi).join(", ")}.`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Panel */}
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
        style={{ willChange: "transform" }}
      >
        {/* Header */}
        <div className="flex items-start gap-3 px-6 py-4 border-b border-gray-100 bg-gray-50/80 flex-shrink-0">
          <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Scale className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold text-gray-900 leading-tight">
              {subItemName}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {pasals.map((p) => p.referensi).join(" · ")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200 flex-shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-5 space-y-6">
          {pasals.map((pasal) => (
            <div key={pasal.key}>
              {/* Pasal reference */}
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xs font-bold text-blue-600 uppercase tracking-wide">
                  {pasal.bab}
                </span>
              </div>

              <h3 className="text-base font-bold text-gray-900 mb-1">
                {pasal.referensi}
              </h3>
              <p className="text-sm font-medium text-gray-600 mb-3">
                {pasal.judul}
              </p>

              {/* Teks pembuka */}
              {pasal.pembuka && (
                <p className="text-sm text-gray-700 leading-relaxed mb-3 italic">
                  {pasal.pembuka}
                </p>
              )}

              {/* Ayat */}
              <div className="space-y-3">
                {pasal.ayat.map((ayat, idx) => {
                  // Label pendek = nomor/huruf seperti "1.", "a.", "(1)", "(2)"
                  const isShortLabel =
                    !ayat.label ||
                    /^(\d+\.|[a-zA-Z]\.|[a-zA-Z]\)|\([0-9a-zA-Z]+\))$/.test(
                      ayat.label.trim(),
                    );

                  // Deteksi lompatan angka antar label berurutan
                  const prevLabel = idx > 0 ? pasal.ayat[idx - 1].label : null;
                  const currNum = extractLabelNumber(ayat.label);
                  const prevNum = prevLabel ? extractLabelNumber(prevLabel) : null;
                  const hasGap = currNum !== null && prevNum !== null && currNum > prevNum + 1;

                  return (
                    <div key={idx}>
                      {hasGap && (
                        <div className="flex gap-3 items-center py-1">
                          <span className="text-gray-300 flex-shrink-0 w-10 text-right text-base leading-none select-none">⋮</span>
                          <span className="text-xs text-gray-400 italic">angka {prevNum + 1}–{currNum - 1} tidak ditampilkan</span>
                        </div>
                      )}
                      {isShortLabel ? (
                        <div className="flex gap-3">
                          {ayat.label && (
                            <span className="text-sm font-bold text-gray-400 flex-shrink-0 w-10 text-right mt-0.5">
                              {ayat.label}
                            </span>
                          )}
                          <div className="flex-1">
                            <p className="text-sm text-gray-700 leading-relaxed">
                              {ayat.teks}
                            </p>
                            {ayat.sub && (
                              <div className="mt-2 space-y-1.5 pl-3 border-l-2 border-gray-200">
                                {ayat.sub.map((s, si) => (
                                  <div key={si} className="flex gap-2">
                                    <span className="text-xs font-bold text-gray-400 flex-shrink-0 w-6 text-right mt-0.5">
                                      {s.label}
                                    </span>
                                    <p className="text-sm text-gray-600 leading-relaxed">
                                      {s.teks}
                                    </p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <span className="inline-flex items-center mb-1.5 px-2 py-0.5 rounded-md bg-blue-50 text-xs font-semibold text-blue-700">
                            {ayat.label}
                          </span>
                          <p className="text-sm text-gray-700 leading-relaxed">
                            {ayat.teks}
                          </p>
                          {ayat.sub && (
                            <div className="mt-2 space-y-1.5 pl-3 border-l-2 border-gray-200">
                              {ayat.sub.map((s, si) => (
                                <div key={si} className="flex gap-2">
                                  <span className="text-xs font-bold text-gray-400 flex-shrink-0 w-6 text-right mt-0.5">
                                    {s.label}
                                  </span>
                                  <p className="text-sm text-gray-600 leading-relaxed">
                                    {s.teks}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Catatan/penjelasan */}
              {pasal.catatan && (
                <div className="mt-4 flex gap-2.5 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
                  <Info className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800 leading-relaxed">
                    <span className="font-semibold">Penjelasan UU: </span>
                    {pasal.catatan}
                  </p>
                </div>
              )}

              {/* Divider antara pasals */}
              {pasals.length > 1 && <hr className="mt-6 border-gray-100" />}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/60 flex-shrink-0">
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <FileText className="h-3.5 w-3.5" />
            <span>LN 2018 No. 147 · TLN No. 6245 · 23 Agustus 2018</span>
          </div>
          {/* <div className="flex items-center gap-2">
            <button
              onClick={() => onAskAI(aiQuestion)}
              className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 transition-colors"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              Tanya AI
            </button>
          </div> */}
        </div>
      </div>
    </div>
  );
}

// ─── Sub-item component ───────────────────────────────────────────────────────

interface SubItemProps {
  item: TreeSubItem;
  color: keyof typeof COLOR_MAP;
  onPasalClick: (item: TreeSubItem, pasals: PasalData[]) => void;
}

function SubItem({ item, color, onPasalClick }: SubItemProps) {
  const [open, setOpen] = useState(false);
  const c = COLOR_MAP[color];
  const hasChildren = (item.children?.length ?? 0) > 0;

  const pasals = useMemo(
    () => (item.pasal ? lookupPasalRefs(item.pasal) : []),
    [item.pasal],
  );
  const hasPasalContent = pasals.length > 0;

  return (
    <div className={clsx("rounded-lg border overflow-hidden", c.border)}>
      <div className="flex items-start gap-2 px-4 py-3">
        {/* Main clickable area */}
        <button
          type="button"
          onClick={() => hasChildren && setOpen((v) => !v)}
          className={clsx(
            "flex-1 flex items-start gap-2 text-left",
            hasChildren ? "cursor-pointer" : "cursor-default",
          )}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-800">
                {item.name}
              </span>
              {item.pasal?.map((p) => (
                <span
                  key={p}
                  className={clsx(
                    "text-xs font-semibold px-2 py-0.5 rounded-full",
                    c.badge,
                  )}
                >
                  {p}
                </span>
              ))}
            </div>
            {item.description && (
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                {item.description}
              </p>
            )}
          </div>
          {hasChildren && (
            <span className="text-gray-400 mt-0.5 flex-shrink-0">
              {open ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </span>
          )}
        </button>

        {/* Lihat Dokumen button */}
        {item.docLink && (
          <Link
            to={item.docLink}
            title="Lihat Struktur Dokumen"
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors flex-shrink-0 bg-violet-50 text-violet-600 hover:bg-violet-100 border border-violet-200"
          >
            <FolderTree className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Dokumen</span>
          </Link>
        )}

        {/* Baca Pasal button */}
        {hasPasalContent && (
          <button
            type="button"
            onClick={() => onPasalClick(item, pasals)}
            title={`Baca teks UU: ${item.pasal?.join(", ")}`}
            className={clsx(
              "flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors flex-shrink-0",
              "bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200",
            )}
          >
            <BookOpen className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Baca</span>
          </button>
        )}
      </div>

      {/* Children */}
      {open && hasChildren && (
        <div className={clsx("border-t", c.border, c.bg, "px-4 py-3")}>
          <ul className="space-y-2">
            {item.children?.map((child, idx) => (
              <li key={idx} className="flex items-start gap-2">
                <span
                  className={clsx(
                    "mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0",
                    c.icon,
                  )}
                />
                <div>
                  <span className="text-sm text-gray-700">{child.name}</span>
                  {child.description && (
                    <p className="text-xs text-gray-500 leading-relaxed">
                      {child.description}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── Category accordion ───────────────────────────────────────────────────────

interface CategoryCardProps {
  category: TreeCategory;
  forceOpen: boolean | null;
  onPasalClick: (item: TreeSubItem, pasals: PasalData[]) => void;
}

function CategoryCard({
  category,
  forceOpen,
  onPasalClick,
}: CategoryCardProps) {
  const [open, setOpen] = useState(false);
  const c = COLOR_MAP[category.color];
  const isOpen = forceOpen !== null ? forceOpen : open;

  const totalItems = category.items.reduce(
    (acc, item) => acc + 1 + (item.children?.length ?? 0),
    0,
  );

  return (
    <div className={clsx("rounded-xl border-2 overflow-hidden", c.border)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div
          className={clsx(
            "h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0",
            c.icon,
          )}
        >
          <span className="text-sm font-bold text-white">{category.id}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-bold text-gray-900">
              {category.title}
            </h3>
            {category.subtitle && (
              <span
                className={clsx(
                  "text-xs font-semibold px-2 py-0.5 rounded-full",
                  c.badge,
                )}
              >
                {category.subtitle}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {category.items.length} sub-topik &middot; {totalItems} item
          </p>
        </div>
        <span className="text-gray-400 flex-shrink-0">
          {isOpen ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
        </span>
      </button>

      {isOpen && (
        <div className={clsx("border-t-2", c.border, "p-5 space-y-3")}>
          {category.items.map((item, idx) => (
            <SubItem
              key={idx}
              item={item}
              color={category.color}
              onPasalClick={onPasalClick}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PnbpStrukturPage() {
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [forceOpen, setForceOpen] = useState<boolean | null>(null);
  const [modal, setModal] = useState<{
    item: TreeSubItem;
    pasals: PasalData[];
  } | null>(null);

  const handlePasalClick = useCallback(
    (item: TreeSubItem, pasals: PasalData[]) => {
      setModal({ item, pasals });
    },
    [],
  );

  const handleAskAI = useCallback(
    (question: string) => {
      // Navigate to knowledge page with pre-filled question via URL state
      navigate("/knowledge", { state: { question } });
    },
    [navigate],
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return PNBP_TREE;
    const q = query.toLowerCase();
    return PNBP_TREE.filter((cat) => {
      if (cat.title.toLowerCase().includes(q)) return true;
      return cat.items.some(
        (item) =>
          item.name.toLowerCase().includes(q) ||
          item.description?.toLowerCase().includes(q) ||
          item.pasal?.some((p) => p.toLowerCase().includes(q)) ||
          item.children?.some(
            (c) =>
              c.name.toLowerCase().includes(q) ||
              c.description?.toLowerCase().includes(q),
          ),
      );
    });
  }, [query]);

  const totalCategories = PNBP_TREE.length;
  const totalSubItems = PNBP_TREE.reduce(
    (acc, cat) => acc + cat.items.length,
    0,
  );
  const totalLeaves = PNBP_TREE.reduce(
    (acc, cat) =>
      acc + cat.items.reduce((a, item) => a + (item.children?.length ?? 0), 0),
    0,
  );
  const totalPasalWithContent = useMemo(() => {
    const keys = new Set<string>();
    PNBP_TREE.forEach((cat) =>
      cat.items.forEach((item) => {
        if (item.pasal) {
          lookupPasalRefs(item.pasal).forEach((p) => keys.add(p.key));
        }
      }),
    );
    return keys.size;
  }, []);

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Modal */}
      {modal && (
        <PasalModal
          pasals={modal.pasals}
          subItemName={modal.item.name}
          onClose={() => setModal(null)}
          onAskAI={handleAskAI}
        />
      )}

      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Struktur Pengetahuan PNBP
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Kerangka pengetahuan PNBP berdasarkan UU No. 9 Tahun 2018 · klik{" "}
          <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded text-xs font-medium border border-blue-200">
            <BookOpen className="h-3 w-3" />
            Baca
          </span>{" "}
          untuk membaca teks resmi dari UU
        </p>
      </div>

      {/* Source badge */}
      <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
        <Scale className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold text-blue-900">Sumber Hukum</p>
          <p className="text-blue-700 mt-0.5">
            <strong>UU No. 9 Tahun 2018</strong> tentang Penerimaan Negara Bukan
            Pajak — disahkan 23 Agustus 2018, LN Tahun 2018 No. 147, TLN No.
            6245. Menggantikan UU No. 20 Tahun 1997.
          </p>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "Kategori Utama", value: totalCategories, color: "blue" },
          { label: "Sub-Topik", value: totalSubItems, color: "indigo" },
          { label: "Poin Detail", value: totalLeaves, color: "violet" },
          {
            label: "Pasal Tersedia",
            value: totalPasalWithContent,
            color: "emerald",
          },
        ].map((stat) => (
          <div
            key={stat.label}
            className="bg-white rounded-xl border border-gray-200 px-3 py-3 text-center"
          >
            <p className="text-xl font-bold text-gray-900">{stat.value}</p>
            <p className="text-xs text-gray-500 mt-0.5 leading-tight">
              {stat.label}
            </p>
          </div>
        ))}
      </div>

      {/* Search + controls */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Cari kategori, sub-topik, atau nomor pasal..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <button
          type="button"
          onClick={() => setForceOpen(true)}
          className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap"
        >
          <ChevronsUpDown className="h-4 w-4" />
          <span className="hidden sm:inline">Buka Semua</span>
        </button>
        <button
          type="button"
          onClick={() => setForceOpen(false)}
          className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 whitespace-nowrap"
        >
          <ChevronsDownUp className="h-4 w-4" />
          <span className="hidden sm:inline">Tutup Semua</span>
        </button>
      </div>

      {/* Tree */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-40 text-gray-400">
          <Search className="h-8 w-8 mb-2 text-gray-300" />
          <p className="text-sm">Tidak ada hasil untuk &ldquo;{query}&rdquo;</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((cat) => (
            <CategoryCard
              key={cat.id}
              category={cat}
              forceOpen={forceOpen}
              onPasalClick={handlePasalClick}
            />
          ))}
        </div>
      )}

      {/* Footer */}
      <p className="text-xs text-gray-400 text-center pb-2">
        Teks pasal bersumber dari salinan resmi UU No. 9 Tahun 2018. Untuk
        verifikasi selalu merujuk ke dokumen hukum resmi di JDIH Kemenkeu.
      </p>
    </div>
  );
}
